import type { ReactNode } from 'react'

import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Overlay'

export interface ConfirmDialogProps {
  open: boolean
  onCancel: () => void
  onConfirm: () => void
  title: string
  /** What will happen, stated in the terms the reader cares about. */
  children: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** `danger` for anything that destroys or overwrites. */
  tone?: 'primary' | 'danger'
  loading?: boolean
}

/**
 * A pause before something that cannot be undone.
 *
 * Used sparingly: a confirmation on every action trains people to click
 * through them, which is worse than having none. This app raises one only for
 * writes to the shared database, which no screen here can reverse.
 */
export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  loading,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      widthClassName="max-w-md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={tone} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="px-4 py-4 text-sm text-ink-secondary">{children}</div>
    </Modal>
  )
}
